import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Grid,
  Alert,
  Divider,
  Typography,
  Box
} from '@mui/material';
import { hitosFacturacionAPI, negociosAPI } from '../services/api';

const SectionBlock = ({ label, children }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', color: '#57606a', whiteSpace: 'nowrap' }}
      >
        {label}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
    {children}
  </Box>
);

const HitoModal = ({ open, onClose, hito, onSave, iniciativas, defaultIniciativaId }) => {
  const [formData, setFormData] = useState({
    iniciativa: '',
    numero_hito: 1,
    nombre: '',
    fecha: '',
    porcentaje: '',
    importe: '',
    cliente: '',
    sociedad_final: '',
    sociedad_cabecera: '',
    zona_geografica: '',
    pais: '',
    facturado: false,
    fecha_facturacion: '',
    observaciones: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [displayImporte, setDisplayImporte] = useState('');
  const [subnegocioOptions, setSubnegocioOptions] = useState([]);
  const [zonaGeograficaOptions, setZonaGeograficaOptions] = useState([]);
  const [paisOptions, setPaisOptions] = useState([]);
  const [busquedaProyecto, setBusquedaProyecto] = useState('');
  const [negocioSociedadMap, setNegocioSociedadMap] = useState({});

  const formatImporte = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = parseFloat(String(value));
    if (isNaN(num)) return '';
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Cargar Sociedad Final del proyecto y calcular siguiente numero_hito al seleccionar iniciativa (solo en creación)
  useEffect(() => {
    if (!hito && formData.iniciativa && iniciativas) {
      const ini = iniciativas.find(i => i.id === Number(formData.iniciativa));
      const sociedadFinal = ini?.nombre_sociedad_comercial || '';

      // Obtener el siguiente numero_hito disponible para el proyecto
      hitosFacturacionAPI.getAll({ iniciativa: formData.iniciativa, page_size: 1000 })
        .then(res => {
          const hitos = res.data.results || res.data || [];
          const maxNumero = hitos.reduce((max, h) => Math.max(max, h.numero_hito || 0), 0);
          setFormData(prev => ({
            ...prev,
            sociedad_final: sociedadFinal,
            numero_hito: maxNumero + 1,
          }));
        })
        .catch(() => {
          setFormData(prev => ({ ...prev, sociedad_final: sociedadFinal }));
        });
    } else if (!formData.iniciativa) {
      setFormData(prev => ({ ...prev, sociedad_final: '' }));
    }
  }, [formData.iniciativa]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar opciones de subnegocio desde la tabla Negocios
  useEffect(() => {
    negociosAPI.getAll().then(res => {
      const negocios = res.data.results || res.data || [];
      const unique = [...new Set(negocios.map(n => n.subnegocio).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
      setSubnegocioOptions(unique);
      // Mapa subnegocio → nombre_sociedad_comercial para auto-rellenar Sociedad Cabecera
      const map = {};
      negocios.forEach(n => {
        if (n.subnegocio && n.nombre_sociedad_comercial) {
          map[n.subnegocio] = n.nombre_sociedad_comercial;
        }
      });
      setNegocioSociedadMap(map);
    }).catch(err => console.error('Error cargando subnegocio opciones:', err));

    hitosFacturacionAPI.getChoices().then(res => {
      setZonaGeograficaOptions(res.data.zona_geografica || []);
      setPaisOptions(res.data.pais || []);
    }).catch(err => console.error('Error cargando choices:', err));
  }, []);

  // Inicializar formulario cuando se abre el modal o cambia el hito
  useEffect(() => {
    if (open) {
      if (hito) {
        // Modo edición
        setFormData({
          iniciativa: hito.iniciativa || '',
          numero_hito: hito.numero_hito || 1,
          nombre: hito.nombre || '',
          fecha: hito.fecha || '',
          porcentaje: hito.porcentaje || '',
          importe: hito.importe !== undefined && hito.importe !== null ? String(hito.importe) : '',
          cliente: hito.cliente || '',
          sociedad_final: hito.sociedad_final || '',
          sociedad_cabecera: hito.sociedad_cabecera || '',
          zona_geografica: hito.zona_geografica || '',
          pais: hito.pais || '',
          facturado: hito.facturado || false,
          fecha_facturacion: hito.fecha_facturacion || '',
          observaciones: hito.observaciones || ''
        });
        setDisplayImporte(formatImporte(hito.importe));
      } else {
        // Modo creación
        setFormData({
          iniciativa: defaultIniciativaId || '',
          numero_hito: 1,
          nombre: '',
          fecha: '',
          porcentaje: '',
          importe: '',
          cliente: '',
          sociedad_final: '',
          sociedad_cabecera: '',
          zona_geografica: '',
          pais: '',
          facturado: false,
          fecha_facturacion: '',
          observaciones: ''
        });
        setDisplayImporte('');
      }
      setErrors({});
      setErrorMessage('');
    }
  }, [open, hito]);

  const handleImporteChange = (e) => {
    const raw = e.target.value;
    setDisplayImporte(raw);
    // Strip thousands dots, convert decimal comma to dot for internal storage
    const numeric = raw.replace(/\./g, '').replace(',', '.');
    setFormData(prev => ({ ...prev, importe: numeric }));
    if (errors.importe) setErrors(prev => ({ ...prev, importe: '' }));
  };

  const handleImporteBlur = () => {
    const num = parseFloat(formData.importe);
    if (!isNaN(num)) {
      setDisplayImporte(formatImporte(num));
      setFormData(prev => ({ ...prev, importe: String(num) }));
    } else {
      setDisplayImporte('');
      setFormData(prev => ({ ...prev, importe: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      // Auto-rellenar País con España cuando se selecciona Zona Geográfica = España
      if (name === 'zona_geografica' && newValue === 'españa') {
        updated.pais = 'españa';
      }
      // Auto-rellenar Sociedad Cabecera al seleccionar Cliente
      if (name === 'cliente' && newValue && negocioSociedadMap[newValue]) {
        updated.sociedad_cabecera = negocioSociedadMap[newValue];
      }
      return updated;
    });
    
    // Limpiar error del campo cuando el usuario lo modifica
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.iniciativa) {
      newErrors.iniciativa = 'Debe seleccionar un proyecto';
    }

    if (!formData.numero_hito || formData.numero_hito < 1) {
      newErrors.numero_hito = 'El número de hito debe ser mayor o igual a 1';
    }

    if (!formData.nombre || formData.nombre.trim() === '') {
      newErrors.nombre = 'El nombre del hito es obligatorio';
    }

    if (formData.porcentaje !== '' && (formData.porcentaje < 0 || formData.porcentaje > 100)) {
      newErrors.porcentaje = 'El porcentaje debe estar entre 0 y 100';
    }

    if (formData.importe !== '' && formData.importe < 0) {
      newErrors.importe = 'El importe no puede ser negativo';
    }

    if (formData.fecha && formData.fecha_facturacion && formData.fecha_facturacion < formData.fecha) {
      newErrors.fecha_facturacion = 'La fecha de facturación no puede ser anterior a la fecha del hito';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      // Preparar datos para enviar
      const dataToSend = {
        iniciativa: parseInt(formData.iniciativa),
        numero_hito: parseInt(formData.numero_hito),
        nombre: formData.nombre,
        fecha: formData.fecha || null,
        porcentaje: formData.porcentaje !== '' ? parseInt(formData.porcentaje) : null,
        importe: formData.importe !== '' ? parseFloat(formData.importe) : null,
        cliente: formData.cliente,
        sociedad_final: formData.sociedad_final || '',
        sociedad_cabecera: formData.sociedad_cabecera || '',
        zona_geografica: formData.zona_geografica || null,
        pais: formData.pais || null,
        facturado: formData.facturado,
        fecha_facturacion: formData.fecha_facturacion || null,
        observaciones: formData.observaciones
      };

      if (hito) {
        // Actualizar hito existente
        await hitosFacturacionAPI.partialUpdate(hito.id, dataToSend);
      } else {
        // Crear nuevo hito
        await hitosFacturacionAPI.create(dataToSend);
      }

      onSave();
    } catch (err) {
      console.error('Error guardando hito:', err);
      
      // Manejar errores de validación del backend
      if (err.response?.data) {
        const backendErrors = err.response.data;
        
        // Si es un error de unique_together (iniciativa + numero_hito)
        if (backendErrors.non_field_errors) {
          setErrorMessage(backendErrors.non_field_errors.join(', '));
        } else {
          // Mapear errores de campo
          const fieldErrors = {};
          Object.keys(backendErrors).forEach(key => {
            if (Array.isArray(backendErrors[key])) {
              fieldErrors[key] = backendErrors[key].join(', ');
            } else {
              fieldErrors[key] = backendErrors[key];
            }
          });
          setErrors(fieldErrors);
          setErrorMessage('Por favor, corrija los errores en el formulario');
        }
      } else {
        setErrorMessage('Error al guardar el hito de facturación');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const iniciativaSeleccionada = iniciativas?.find(i => i.id === Number(formData.iniciativa));
  const importeClienteHint = (!hito && iniciativaSeleccionada?.importe_total_cliente != null)
    ? `Precio de venta del proyecto: ${parseFloat(iniciativaSeleccionada.importe_total_cliente).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}`
    : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
        {hito ? 'Editar Hito de Facturación' : 'Nuevo Hito de Facturación'}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2.5 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── Identificación ── */}
            <SectionBlock label="Identificación">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Proyecto"
                    name="iniciativa"
                    value={formData.iniciativa}
                    onChange={handleChange}
                    error={!!errors.iniciativa}
                    helperText={errors.iniciativa}
                    required
                    size="small"
                    disabled={loading || !!hito}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: { autoFocus: false },
                      onClose: () => setBusquedaProyecto('')
                    }}
                  >
                    <MenuItem
                      disableRipple
                      onKeyDown={e => e.stopPropagation()}
                      sx={{ p: 0, '&:hover': { background: 'transparent' }, '&.Mui-focusVisible': { background: 'transparent' } }}
                    >
                      <TextField
                        autoFocus
                        placeholder="Buscar proyecto..."
                        size="small"
                        fullWidth
                        value={busquedaProyecto}
                        onChange={e => setBusquedaProyecto(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        sx={{ px: 1, py: 0.5 }}
                        inputProps={{ style: { fontSize: '0.875rem' } }}
                      />
                    </MenuItem>
                    <MenuItem value=""><em style={{ color: '#9e9e9e' }}>Seleccione un proyecto</em></MenuItem>
                    {(iniciativas || [])
                      .filter(ini =>
                        !busquedaProyecto ||
                        ini.nombre_de_la_oportunidad.toLowerCase().includes(busquedaProyecto.toLowerCase())
                      )
                      .map((ini) => (
                        <MenuItem key={ini.id} value={ini.id}>
                          {ini.nombre_de_la_oportunidad}
                        </MenuItem>
                      ))
                    }
                  </TextField>
                </Grid>
                {formData.sociedad_final !== undefined && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Sociedad Final"
                      name="sociedad_final"
                      value={formData.sociedad_final}
                      onChange={handleChange}
                      size="small"
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                      helperText="Cargada automáticamente del proyecto. Puede modificarla."
                    />
                  </Grid>
                )}
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    type="number"
                    label="N.º Hito"
                    name="numero_hito"
                    value={formData.numero_hito}
                    onChange={handleChange}
                    error={!!errors.numero_hito}
                    helperText={errors.numero_hito}
                    required
                    size="small"
                    inputProps={{ min: 1, step: 1 }}
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha Prevista"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleChange}
                    error={!!errors.fecha}
                    helperText={errors.fecha}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre del Hito"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    error={!!errors.nombre}
                    helperText={errors.nombre}
                    required
                    size="small"
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </SectionBlock>

            {/* ── Datos Económicos ── */}
            <SectionBlock label="Datos Económicos">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Cliente"
                    name="cliente"
                    value={formData.cliente}
                    onChange={handleChange}
                    error={!!errors.cliente}
                    helperText={errors.cliente}
                    size="small"
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em style={{ color: '#9e9e9e' }}>— Sin cliente —</em></MenuItem>
                    {subnegocioOptions.map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sociedad Cabecera"
                    name="sociedad_cabecera"
                    value={formData.sociedad_cabecera}
                    onChange={handleChange}
                    error={!!errors.sociedad_cabecera}
                    helperText={errors.sociedad_cabecera || 'Se carga automáticamente al seleccionar el cliente. Puede modificarla.'}
                    size="small"
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Porcentaje (%)"
                    name="porcentaje"
                    value={formData.porcentaje}
                    onChange={handleChange}
                    error={!!errors.porcentaje}
                    helperText={errors.porcentaje}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    size="small"
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <TextField
                    fullWidth
                    type="text"
                    inputMode="decimal"
                    label="Importe (€)"
                    name="importe"
                    value={displayImporte}
                    onChange={handleImporteChange}
                    onBlur={handleImporteBlur}
                    error={!!errors.importe}
                    helperText={errors.importe || importeClienteHint || ''}
                    size="small"
                    disabled={loading}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Zona Geográfica"
                    name="zona_geografica"
                    value={formData.zona_geografica}
                    onChange={handleChange}
                    error={!!errors.zona_geografica}
                    helperText={errors.zona_geografica}
                    size="small"
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em style={{ color: '#9e9e9e' }}>— Sin zona —</em></MenuItem>
                    {zonaGeograficaOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    select
                    label="País"
                    name="pais"
                    value={formData.pais}
                    onChange={handleChange}
                    error={!!errors.pais}
                    helperText={errors.pais}
                    size="small"
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em style={{ color: '#9e9e9e' }}>— Sin país —</em></MenuItem>
                    {paisOptions.map(({ value, label }) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </SectionBlock>

            {/* ── Estado de Facturación ── */}
            <SectionBlock label="Estado de Facturación">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="facturado"
                        checked={formData.facturado}
                        onChange={handleChange}
                        disabled={loading}
                        size="small"
                      />
                    }
                    label="Facturado"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Fecha de Facturación"
                    name="fecha_facturacion"
                    value={formData.fecha_facturacion}
                    onChange={handleChange}
                    error={!!errors.fecha_facturacion}
                    helperText={errors.fecha_facturacion || (!formData.facturado ? 'Marcar como facturado para habilitar' : '')}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    disabled={loading || !formData.facturado}
                  />
                </Grid>
              </Grid>
            </SectionBlock>

            {/* ── Observaciones ── */}
            <SectionBlock label="Observaciones">
              <TextField
                fullWidth
                multiline
                rows={3}
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                error={!!errors.observaciones}
                helperText={errors.observaciones}
                size="small"
                disabled={loading}
                placeholder="Detalles adicionales sobre este hito..."
              />
            </SectionBlock>

          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined" color="inherit">
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default HitoModal;
